if (!window.CusaCountdownTimer) {
	/**
	 * Web component for rendering a configurable countdown timer.
	 *
	 * Attributes:
	 * - `data-config`: JSON string specifying display options:
	 *     - show: string[] – which units to show (e.g. ["minutes", "seconds"])
	 *     - order: string[] – order of units (e.g. ["minutes", "seconds"])
	 *     - labelStyle: "full" | "short" | "delimiter"
	 *     - separator: string – e.g. ":", ", ", etc.
	 *     - leadingZeros: boolean – pad units to 2 digits
	 * - `data-end-time`: End time in milliseconds (or seconds, auto-scaled)
	 * - `data-wrapper-selector`: Optional CSS selector for a wrapper element to hide when countdown reaches zero
	 *
	 * Behavior:
	 * - Starts automatically on page load.
	 * - Updates every second.
	 * - When countdown ends, hides specified wrapper and clears timer.
	 */
	class CusaCountdownTimer extends HTMLElement {
		/**
		 * Initializes internal state, default configuration, and label mappings.
		 */
		constructor() {
			super();

			this._endTime = null;
			this._interval = null;

			this._defaultConfig = {
				show: ['days', 'hours', 'minutes', 'seconds'],
				order: ['days', 'hours', 'minutes', 'seconds'],
				labelStyle: 'full', // 'full' | 'short' | 'delimiter'
				leadingZeros: true,
				separator: ':'
			};

			this._labelVariants = {
				full: {
					days: 'days',
					hours: 'hours',
					minutes: 'minutes',
					seconds: 'seconds'
				},
				short: {
					days: 'd',
					hours: 'hrs',
					minutes: 'mins',
					seconds: 'secs'
				},
				delimiter: {
					days: '',
					hours: '',
					minutes: '',
					seconds: ''
				}
			};
		}

		/**
		 * Lifecycle method called when the element is added to the DOM.
		 * Parses attributes, renders markup, and starts the countdown.
		 */
		connectedCallback() {
			this._parseAttributes();
			this._render();
			this._startTimer();
		}

		/**
		 * Lifecycle method called when the element is removed from the DOM.
		 * Clears the interval timer to prevent memory leaks.
		 */
		disconnectedCallback() {
			clearInterval(this._interval);
		}

		/**
		 * Parses `data-end-time` and `data-config` attributes.
		 * Applies default values and merges with any custom config.
		 */
		_parseAttributes() {
			const endTimeAttr = this.dataset.endTime;
			this._endTime = endTimeAttr ? parseInt(endTimeAttr, 10) : null;

			// Auto-scale seconds → ms. The threshold sits between the largest
			// realistic seconds timestamp (~1e10, year ~5138) and the smallest
			// realistic ms timestamp (~1e12, year ~2001), so seconds inputs are
			// scaled while genuine ms inputs are left alone. (The previous 2e12
			// threshold wrongly re-scaled current ms timestamps, which are ~1.78e12.)
			if (this._endTime && this._endTime < 100000000000) {
				this._endTime *= 1000;
			}

			let configAttr = {};
			try {
				configAttr = JSON.parse(this.dataset.config || '{}');
				console.group(`
⏰ cusa-countdown-timer ⏰`);
				console.log(`
The countdown timer for ${this.dataset.timerId} will reach 00:00:00 at ${new Date(this._endTime).toLocaleString()}`);
				console.log(configAttr);
				if (this.dataset.wrapperSelector) {
					console.log(`Wrapper selector to hide at countdown end: ${this.dataset.wrapperSelector}`);
				}
				console.groupEnd('cusa-countdown-timer');
			} catch (e) {
				console.warn('Invalid config JSON:', e);
			}

			this._config = {
				...this._defaultConfig,
				...configAttr
			};

			this._labels = this._labelVariants[this._config.labelStyle] || this._labelVariants.full;
		}

		/**
		 * Formats a time unit with or without leading zeros depending on config.
		 * @param {number} unit - The time unit value (e.g., hours, minutes).
		 * @returns {string} - Formatted string.
		 */
		_format(unit) {
			return this._config.leadingZeros ? String(unit).padStart(2, '0') : String(unit);
		}

		/**
		 * Renders the initial HTML structure and immediately updates the display.
		 */
		_render() {
			this.innerHTML = `
				<span id="countdown" style="display: inline-block; font-family: inherit; font-size: inherit;"></span>
			`;
			this._updateDisplay();
		}

		/**
		 * Starts the interval timer to update the countdown every second.
		 * Skips setup if no end time is defined.
		 */
		_startTimer() {
			if (!this._endTime) return;

			this._interval = setInterval(() => {
				this._updateDisplay();
			}, 1000);
			this._updateDisplay();
		}

		/**
		 * Calculates remaining time and updates the visual display.
		 * Hides the parent wrapper if countdown reaches zero.
		 */
		_updateDisplay() {
			const now = Date.now();
			let remaining = Math.floor((this._endTime - now) / 1000);

			// console.log('[Countdown] now:', now);
			// console.log('[Countdown] end:', this._endTime);
			// console.log('[Countdown] remaining seconds:', remaining);

			const countdownElem = this.querySelector('#countdown');

			if (remaining <= 0) {
				clearInterval(this._interval);

				const wrapperSelector = this.dataset.wrapperSelector;
				const wrapper = wrapperSelector ? document.querySelector(wrapperSelector) : null;
				if (wrapper) wrapper.classList.add('hidden');

				if (countdownElem) {
					countdownElem.textContent = '';
				}

				// Announce countdown end for screen readers
				const liveRegion = document.createElement('div');
				liveRegion.setAttribute('role', 'status');
				liveRegion.className = 'visually-hidden';
				liveRegion.textContent = 'The countdown has ended.';
				document.body.appendChild(liveRegion);
				setTimeout(() => liveRegion.remove(), 5000);

				return;
			}

			const days = Math.floor(remaining / 86400);
			remaining %= 86400;
			const hours = Math.floor(remaining / 3600);
			remaining %= 3600;
			const minutes = Math.floor(remaining / 60);
			const seconds = remaining % 60;

			const allUnits = { days, hours, minutes, seconds };
			const parts = [];

			const useLabels = this._config.labelStyle !== 'delimiter';

			for (const unit of this._config.order) {
				if (!this._config.show.includes(unit)) continue;
				const val = this._format(allUnits[unit]);
				const label = this._labels[unit];
				parts.push(useLabels ? `${val} ${label}` : val);
			}

			if (countdownElem) {
				const output = parts.join(this._config.separator);
				countdownElem.textContent = output;
				countdownElem.setAttribute('aria-label', output);
			}
		}
	}

	window.CusaCountdownTimer = CusaCountdownTimer;

	// Prevents "already defined" errors during hot reload in local Shopify development.
	if (!customElements.get('cusa-countdown-timer')) {
		customElements.define('cusa-countdown-timer', CusaCountdownTimer);
	}
}
